import { api } from './api';

const permissionsService = {
  /**
   * Get all permissions for the current user
   * @returns {Promise<Object>} Object with permission names as keys and boolean values
   */
  getUserPermissions: async () => {
    console.debug('[PERMISSIONS] Fetching user permissions...');
    try {
      const response = await api.get('/api/user/permissions/');
      console.debug('[PERMISSIONS] API response status:', response.status);
      console.debug('[PERMISSIONS] User permissions received:', response.data);
      console.debug('[PERMISSIONS] Data type:', typeof response.data);
      console.debug('[PERMISSIONS] Data keys:', Object.keys(response.data || {}));
      return response.data;
    } catch (error) {
      console.error('[PERMISSIONS] Error fetching user permissions:', error);
      console.error('[PERMISSIONS] Error response:', error.response?.data);
      console.error('[PERMISSIONS] Error status:', error.response?.status);
      console.error('[PERMISSIONS] Error message:', error.message);
      return {};
    }
  },

  /**
   * Check if user has a specific permission
   * @param {string} permissionName - The name of the permission to check (e.g., 'can_select_sponsor')
   * @returns {Promise<boolean>} Whether the user has the permission
   */
  hasPermission: async (permissionName) => {
    console.debug(`[PERMISSIONS] Checking permission: ${permissionName}`);
    try {
      const response = await api.get(`/api/user/permissions/${permissionName}/`);
      const hasPermission = response.data.has_permission === true;
      console.debug(`[PERMISSIONS] Permission ${permissionName} check result: ${hasPermission}`);
      return hasPermission;
    } catch (error) {
      console.error(`[PERMISSIONS] Error checking permission ${permissionName}:`, error);
      return false;
    }
  },

  /**
   * Convenience constants for permission names
   */
  PERMISSIONS: {
    SELECT_SPONSOR: 'can_select_sponsor',
    ACCESS_STUDY_BUILDER: 'can_access_study_builder',
    ACCESS_ASSESSMENT_BUILDER: 'can_access_assessment_builder',
    ACTIVATE_SURVEY_WINDOW: 'can_activate_survey_window',
    MANAGE_STUDY_SECURITY: 'manage_study_security',
    CHANGE_STUDY: 'change_study',
    ADD_USER: 'add_user',
    ADD_SITE: 'add_site',
    CHANGE_SITE: 'change_site',
    ADD_PARTICIPANT: 'can_add_participant',
    ACTIVATE_PARTICIPANT: 'activate_participant',
    DEACTIVATE_PARTICIPANT: 'deactivate_participant',
    // Assessment Builder permissions
    PUBLISH_TO_VPG_LIBRARY: 'can_publish_to_vpg_library',
    EDIT_SPONSOR_LIBRARY: 'can_edit_sponsor_library',
    DELETE_ASSESSMENT: 'can_delete_assessment',
    // VPG Item Bank permissions (granular)
    CREATE_VPG_ITEM: 'can_create_vpg_item',
    PUBLISH_VPG_ITEM: 'can_publish_vpg_item',
    DELETE_VPG_ITEM: 'can_delete_vpg_item',
    // DCQ (Data Clarification Query) permissions
    VIEW_DCQ: 'can_view_dcq',
    FILE_DCQ: 'can_file_dcq',
    COMMENT_ON_DCQ: 'can_comment_on_dcq',
    ALTER_DCQ: 'can_alter_dcq',
    VIEW_ALL_DCQS_FOR_STUDY: 'can_view_all_dcqs_for_study',
    IMPLEMENT_DCQ: 'can_implement_dcq',
    VIEW_DCQ_COMMENTS: 'can_view_dcq_comments',
    // Data access permissions
    VIEW_DATA: 'can_view_data',
    EDIT_DATA: 'can_edit_data',
    // Report permissions
    VIEW_REPORTS: 'can_view_reports',
    EXPORT_REPORTS: 'can_export_reports',
    // Bulk participant management
    BULK_CREATE_PARTICIPANTS: 'can_bulk_create_participants',
    BULK_EDIT_PARTICIPANTS: 'can_bulk_edit_participants',
    BULK_DELETE_PARTICIPANTS: 'can_bulk_delete_participants',
  }
};

export default permissionsService;
